import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Input, Spin, Divider } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield, faShieldHalved, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { usePutRoleMutation } from "../../../redux/roles/roleAPI";
import { updateOpenModalEditRole } from "../../../redux/roles/roleSlice";
// COMPONENTS
import WizardPermissionTreeEditor from "../../setupWizard/components/WizardPermissionTreeEditor";
import GlobalAlert2 from "../../home/GlobalAlert2";
import { withMissingPermissions } from "./roleSaveError";
import ButtonClose from "../../components/buttons/ButtonClose";
import ButtonSave from "../../components/buttons/ButtonSave";
import RoleScopeEditor from "../../../components/scopeEditor/RoleScopeEditor";
import RolePermissionHistory from "./RolePermissionHistory";
// ROLE MENU (FlatBuffer Object API — same model the wizard editor uses)
import {
  safeParseRoleMenu,
  roleMenuSaveJson,
  readRolePermissionGrants,
} from "../../setupWizard/utils/roleMenu";
import type { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UUID — role.id / role.orgId decode to FlatBuffer UuidT objects, not strings
import { formatUuidT } from "../../../flatbuffers/display";
// SCOPE
import { extractRoleScopeGrants } from "../../../redux/scope/extractRoleScopeGrants";
import { grantsToBodyArrays } from "../../../redux/scope/scopeSerialization";
import type { RoleScopeGrants } from "../../../redux/scope/scopeSlice";
// PERMISSIONS
import { Permission, hasPermission } from "../../../types/permissions";

const EMPTY_SCOPE_GRANTS: RoleScopeGrants = {
  bankGrants: [],
  liftGrants: [],
  floorGrants: [],
};

function RolesEditRoleForm() {
  // ************************************************ */
  // LOCAL STORAGE AND VARIABLES ******************** */
  const dispatch = useDispatch();
  const { role, openModalEditRole } = useSelector((state: any) => state.role);
  const [t] = useTranslation("global");
  // The role's full permission menu (FlatBuffer Object API). The roles LIST
  // response already carries the whole menu as `menuJson` (a JSON string of
  // every page/sub-page/action with this role's active + offline flags), so we
  // parse it directly. `role.menu` decodes to null and `role.id` is a UUID
  // object (not a string), so an on-open getRole fetch is neither needed nor
  // possible here.
  const [editMenu, setEditMenu] = useState<RoleMenuT | null>(null);
  const { theme } = useSelector((state: any) => state.home);
  const { permissionSet } = useSelector((state: any) => state.user);
  // The history panel needs its own permission, which LiftCloud grants by
  // rule to every role that can view roles.
  const canViewHistory = hasPermission(
    Permission.RolesPermissionHistoryView,
    (permissionSet as string[] | undefined) ?? [],
  );
  const [roleName, setRoleName] = useState("");
  const [scopeGrants, setScopeGrants] = useState<RoleScopeGrants>(EMPTY_SCOPE_GRANTS);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    putRole,
    {
      isSuccess: isSuccessPutRole,
      isError: isErrorPutRole,
      error: errorPutRole,
      isLoading: isLoadingPutRole,
      reset: resetPutRole,
    },
  ] = usePutRoleMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const onClickClose = () => {
    dispatch(updateOpenModalEditRole(false));
  };

  const onClickSave = async () => {
    // Guard: never save without a loaded menu. The save rebuilds the role's
    // permissions from editMenu — persisting an empty/unloaded menu would clear
    // every permission on the role.
    if (!editMenu) return;
    const token = await GETJwtToken();
    // Every node's switches become explicit decisions, over the role's
    // existing ones (see roleMenuSaveJson).
    const menuObj = roleMenuSaveJson(editMenu, readRolePermissionGrants(role));
    menuObj.name = roleName;
    // role.id and role.orgId decode to FlatBuffer UuidT objects ({value:{upper,
    // lower}}) — the PUT URL and org_id both need the canonical UUID string.
    putRole({
      id: formatUuidT(role.id),
      token,
      body: {
        active: role.active,
        name: roleName,
        display_name: roleName,
        org_id: formatUuidT(role.orgId),
        menu: menuObj,
        ...grantsToBodyArrays(scopeGrants),
      },
    });
  };

  const onClose = () => {
    dispatch(updateOpenModalEditRole(false));
    setRoleName("");
    setEditMenu(null);
    setScopeGrants(EMPTY_SCOPE_GRANTS);
  };

  const onFill = () => {
    if (!role) return;
    setRoleName(role.name);
    // Seed scope grants from the role data (may carry bank/lift/floor arrays)
    setScopeGrants(extractRoleScopeGrants(role));
    // Parse the full permission menu the list response already carries: `menu`
    // (the whole catalogue with the role's effective flags), else menuJson.
    setEditMenu(safeParseRoleMenu(role?.menu ?? role?.menuJson));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */

  useEffect(() => {
    if (openModalEditRole) {
      onFill();
      resetPutRole();
    }
  }, [openModalEditRole]);

  useEffect(() => {
    dispatch(updateOpenModalEditRole(false));
  }, [isSuccessPutRole, isErrorPutRole]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width={550}
      placement="right"
      onClose={onClose}
      closable={false}
      open={openModalEditRole}
      footer={
        <div className="container generalStyles__flex mt-4">
          <div className="generalStyles__width100 generalStyles__mrFix">
            <ButtonClose onClick={onClickClose} />
          </div>
          <div className="generalStyles__width100 generalStyles__mlFix">
            <ButtonSave onClick={onClickSave} disabledButton={false} isLoading={false} />
          </div>
        </div>
      }
    >
      <Spin spinning={isLoadingPutRole} tip={t("general.loading")}>
        <div>
          <Divider orientation="left" className="generalStyles__drawerDivider">
            <h5>
              <FontAwesomeIcon
                icon={faUserShield}
                className="generalStyles__info generalStyles__mrFix"
              />
              {t("organizationManagement.editRole")}
            </h5>
          </Divider>
        </div>
        <div className={`drawer__box__${theme}`}>
          <div className="col-md-12">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexName">
                <span className="generalStyles__inputFlexRequired">*</span>
                <span className="drawer__text">{t("organizationManagement.roleName")}</span>
              </div>
              <div className="generalStyles__width100">
                <Input
                  className="generalStyles__input"
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    setRoleName(event.currentTarget.value);
                  }}
                  value={roleName}
                  size="small"
                />
              </div>
              <div className="generalStyles__infoTooltipIconBlank" />
            </div>
          </div>
        </div>
        <br />
        {/** ************************************************* */}
        <div>
          <Divider orientation="left" className="generalStyles__drawerDivider">
            <h5>
              <FontAwesomeIcon
                icon={faUserShield}
                className="generalStyles__info generalStyles__mrFix"
              />
              {t("organizationManagement.permissions")}
            </h5>
          </Divider>
        </div>
        <div className={`drawer__box__${theme}`}>
          <WizardPermissionTreeEditor menu={editMenu} onChange={setEditMenu} />
        </div>
        <br />

        {/** ************************************************* */}
        <div>
          <Divider orientation="left" className="generalStyles__drawerDivider">
            <h5>
              <FontAwesomeIcon
                icon={faShieldHalved}
                className="generalStyles__info generalStyles__mrFix"
              />
              {t("general.scopeEditorTitle")}
            </h5>
          </Divider>
        </div>
        <div className={`drawer__box__${theme}`}>
          <RoleScopeEditor
            value={scopeGrants}
            onChange={setScopeGrants}
            orgId={formatUuidT(role.orgId)}
          />
        </div>

        {/** ************************************************* */}
        {canViewHistory && openModalEditRole && role?.id && (
          <div data-testid="roles-edit-permission-history-section">
            <br />
            <div>
              <Divider orientation="left" className="generalStyles__drawerDivider">
                <h5>
                  <FontAwesomeIcon
                    icon={faClockRotateLeft}
                    className="generalStyles__info generalStyles__mrFix"
                  />
                  {t("organizationManagement.permissionHistory.title")}
                </h5>
              </Divider>
            </div>
            <div className={`drawer__box__${theme}`}>
              <RolePermissionHistory roleId={formatUuidT(role.id)} />
            </div>
          </div>
        )}
      </Spin>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPutRole}
        isSuccess={isSuccessPutRole}
        requestType="PUT"
        error={withMissingPermissions(errorPutRole, t("organizationManagement.missingPermissions"))}
        name="Role"
      />
    </Drawer>
  );
}

export default RolesEditRoleForm;
